import { StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * <nb-cart-bar> - the site-wide sticky bar shown at the bottom of the
 * viewport whenever the cart has at least one item.
 *
 * Server-rendered already correct for the current request (see
 * nb-cart-bar.liquid); this only keeps it in sync afterwards. It listens for
 * the same `shopify:cart:lines-update` event every cart control on the site
 * already dispatches (nb-cart-stepper, quantity selectors, quick add, the
 * variant picker modal, ...), so nothing that adds/removes/changes a line
 * needs to know this bar exists.
 *
 * Rather than trust whatever shape the event's own promise resolves with
 * (it varies a little by source), it re-fetches /cart.js itself once the
 * mutation settles - the same raw AJAX cart endpoint every other cart
 * control in the theme reads from - so the count and total shown here are
 * always read straight from Shopify's cart, never derived/estimated.
 */
class NbCartBar extends HTMLElement {
  connectedCallback() {
    this.countEl = this.querySelector('[data-nb-cart-bar-count]');
    this.itemsEl = this.querySelector('[data-nb-cart-bar-items]');
    this.totalEl = this.querySelector('[data-nb-cart-bar-total]');
    // Merchant-editable wording (Theme Editor > Sticky cart bar) read back off
    // the server-rendered element, so a client-side refresh still uses it
    // instead of falling back to a hardcoded English string.
    this.itemLabelSingular = this.dataset.itemLabelSingular || 'item';
    this.itemLabelPlural = this.dataset.itemLabelPlural || 'items';

    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * @param {PageTransitionEvent} event
   */
  onPageShow = (event) => {
    // bfcache restore: the cart may have changed in another tab since this
    // page was cached, and no cartLinesUpdate event will fire to tell us.
    if (event.persisted) this.refresh();
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  onCartUpdate = (event) => {
    event.promise
      ?.then(() => this.refresh())
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[nb-cart-bar] Event promise rejected:', error);
      });
  };

  async refresh() {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      this.render(cart.item_count, cart.total_price, cart.currency);
    } catch (error) {
      console.warn('[nb-cart-bar] Failed to refresh cart totals:', error);
    }
  }

  /**
   * @param {number} itemCount
   * @param {number} totalPriceCents
   * @param {string} currency
   */
  render(itemCount, totalPriceCents, currency) {
    const itemLabel = itemCount === 1 ? this.itemLabelSingular : this.itemLabelPlural;

    if (this.countEl) this.countEl.textContent = String(itemCount);
    if (this.itemsEl) this.itemsEl.textContent = `${itemCount} ${itemLabel}`;
    if (this.totalEl) this.totalEl.textContent = formatMoney(totalPriceCents, Theme.moneyFormat, currency);

    this.hidden = itemCount === 0;
  }
}

if (!customElements.get('nb-cart-bar')) {
  customElements.define('nb-cart-bar', NbCartBar);
}
